import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  ArrowLeft, Plus, Trash, FloppyDisk, CircleNotch, PlayCircle, FileText,
  Paperclip, BookOpen, ListChecks, UploadSimple, DotsSixVertical,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const emptyLesson = () => ({ id: crypto.randomUUID(), title: "", type: "video", content: "", video_url: "", file_url: "", file_name: "", resources: [], duration_min: 10 });
const emptyModule = () => ({ id: crypto.randomUUID(), title: "", lessons: [emptyLesson()] });
const emptyQuestion = () => ({ id: crypto.randomUUID(), type: "multiple", text: "", options: ["", "", "", ""], correct_index: 0 });

export default function CourseBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === "new";
  const [form, setForm] = useState({
    title: "", description: "", category: "General", modality: "online", duration_hours: 10,
    price: 50000, image_url: "", passing_grade: 4.0, min_attendance: 75, published: false,
    objectives: [], syllabus: [], modules: [emptyModule()], quiz: [emptyQuestion()],
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew) return;
    api.get(`/courses/${id}`).then((r) => {
      const c = r.data;
      setForm({
        ...c,
        objectives: c.objectives || [], syllabus: c.syllabus || [],
        modules: (c.modules?.length ? c.modules : [emptyModule()]),
        quiz: (c.quiz?.length ? c.quiz : [emptyQuestion()]),
      });
    }).catch(() => navigate("/dashboard")).finally(() => setLoading(false));
  }, [id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // ---- modules / lessons ----
  const updModule = (mi, patch) => set("modules", form.modules.map((m, i) => i === mi ? { ...m, ...patch } : m));
  const updLesson = (mi, li, patch) => updModule(mi, { lessons: form.modules[mi].lessons.map((l, i) => i === li ? { ...l, ...patch } : l) });
  const addModule = () => set("modules", [...form.modules, emptyModule()]);
  const delModule = (mi) => set("modules", form.modules.filter((_, i) => i !== mi));
  const addLesson = (mi) => updModule(mi, { lessons: [...form.modules[mi].lessons, emptyLesson()] });
  const delLesson = (mi, li) => updModule(mi, { lessons: form.modules[mi].lessons.filter((_, i) => i !== li) });

  const uploadFile = async (mi, li, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      if (form.modules[mi].lessons[li].type === "video") updLesson(mi, li, { video_url: url });
      else updLesson(mi, li, { file_url: url, file_name: file.name });
      toast.success("Archivo subido");
    } catch { toast.error("Error al subir archivo"); }
  };

  const uploadResource = async (mi, li, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      const res = [...(form.modules[mi].lessons[li].resources || []), { name: file.name, url }];
      updLesson(mi, li, { resources: res });
      toast.success("Recurso adjuntado");
    } catch { toast.error("Error al subir recurso"); }
  };
  const delResource = (mi, li, ri) => updLesson(mi, li, { resources: (form.modules[mi].lessons[li].resources || []).filter((_, i) => i !== ri) });

  // ---- quiz ----
  const updQ = (qi, patch) => set("quiz", form.quiz.map((q, i) => i === qi ? { ...q, ...patch } : q));
  const addQ = () => set("quiz", [...form.quiz, emptyQuestion()]);
  const delQ = (qi) => set("quiz", form.quiz.filter((_, i) => i !== qi));

  const save = async () => {
    if (!form.title.trim()) { toast.error("El curso necesita un título"); return; }
    setSaving(true);
    const payload = {
      ...form,
      duration_hours: parseInt(form.duration_hours) || 0,
      price: parseFloat(form.price) || 0,
      passing_grade: parseFloat(form.passing_grade) || 4,
      min_attendance: parseFloat(form.min_attendance) || 0,
      objectives: Array.isArray(form.objectives) ? form.objectives : String(form.objectives).split("\n").filter(Boolean),
      syllabus: Array.isArray(form.syllabus) ? form.syllabus : String(form.syllabus).split("\n").filter(Boolean),
      modules: form.modules.map((m, mi) => ({ ...m, order: mi + 1, lessons: m.lessons.map((l, li) => ({ ...l, order: li + 1, duration_min: parseInt(l.duration_min) || 5 })) })),
      quiz: form.quiz.filter((q) => q.text.trim()).map((q) => ({ ...q, options: q.type === "truefalse" ? ["Verdadero", "Falso"] : q.options })),
    };
    try {
      if (isNew) { const { data } = await api.post("/courses", payload); toast.success("Curso creado"); navigate(`/dashboard/curso-editor/${data.id}`); }
      else { await api.put(`/courses/${id}`, payload); toast.success("Contenido guardado"); }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><CircleNotch size={32} className="animate-spin text-primary" /></div>;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 bg-[#1E293B] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-sm text-slate-300 hover:text-white"><ArrowLeft size={18} /> Volver</Link>
          <span className="text-sm font-medium truncate">{isNew ? "Nuevo curso" : "Editar contenido"}</span>
          <Button onClick={save} disabled={saving} size="sm" data-testid="builder-save" className="bg-primary hover:bg-blue-600 text-white gap-2">
            {saving ? <CircleNotch className="animate-spin" size={16} /> : <FloppyDisk size={16} />} Guardar
          </Button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {/* Basic info */}
        <section className="bg-white border border-border rounded-lg p-6">
          <h2 className="font-display text-xl font-800 tracking-tight mb-4 flex items-center gap-2"><BookOpen size={20} className="text-primary" /> Información del curso</h2>
          <div className="grid gap-4">
            <div><Label>Título del curso</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="builder-title" className="mt-1" /></div>
            <div><Label>Descripción</Label><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="builder-desc" className="mt-1" /></div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div><Label>Categoría</Label><Input value={form.category} onChange={(e) => set("category", e.target.value)} className="mt-1" /></div>
              <div><Label>Modalidad</Label>
                <Select value={form.modality} onValueChange={(v) => set("modality", v)}>
                  <SelectTrigger className="mt-1" data-testid="builder-modality"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="online">Online</SelectItem><SelectItem value="presencial">Presencial</SelectItem><SelectItem value="mixta">Mixta</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Horas</Label><Input type="number" value={form.duration_hours} onChange={(e) => set("duration_hours", e.target.value)} className="mt-1" /></div>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div><Label>Precio (CLP)</Label><Input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} className="mt-1" /></div>
              <div><Label>Nota mínima (1-7)</Label><Input type="number" step="0.1" value={form.passing_grade} onChange={(e) => set("passing_grade", e.target.value)} className="mt-1" /></div>
              <div><Label>Asistencia mín. (%)</Label><Input type="number" value={form.min_attendance} onChange={(e) => set("min_attendance", e.target.value)} className="mt-1" /></div>
            </div>
            <div><Label>URL imagen de portada</Label><Input value={form.image_url} onChange={(e) => set("image_url", e.target.value)} className="mt-1" placeholder="https://..." /></div>
            <div><Label>Objetivos (uno por línea)</Label><Textarea value={Array.isArray(form.objectives) ? form.objectives.join("\n") : form.objectives} onChange={(e) => set("objectives", e.target.value.split("\n"))} className="mt-1" /></div>
            <div><Label>Temario / contenidos (uno por línea)</Label><Textarea value={Array.isArray(form.syllabus) ? form.syllabus.join("\n") : form.syllabus} onChange={(e) => set("syllabus", e.target.value.split("\n"))} className="mt-1" /></div>
            <label className="flex items-center gap-3 cursor-pointer">
              <Switch checked={form.published} onCheckedChange={(v) => set("published", v)} data-testid="builder-published" />
              <span className="text-sm font-medium">{form.published ? "Publicado (visible en catálogo)" : "Borrador (oculto)"}</span>
            </label>
          </div>
        </section>

        {/* Modules & lessons */}
        <section className="bg-white border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-800 tracking-tight flex items-center gap-2"><PlayCircle size={20} className="text-primary" /> Módulos y lecciones</h2>
            <Button onClick={addModule} variant="outline" size="sm" className="gap-2" data-testid="builder-add-module"><Plus size={16} /> Módulo</Button>
          </div>
          <div className="space-y-5">
            {form.modules.map((m, mi) => (
              <div key={m.id} className="border border-border rounded-lg p-4 bg-secondary/30" data-testid={`builder-module-${mi}`}>
                <div className="flex items-center gap-2 mb-3">
                  <DotsSixVertical size={18} className="text-muted-foreground" />
                  <Input value={m.title} onChange={(e) => updModule(mi, { title: e.target.value })} placeholder={`Módulo ${mi + 1}`} className="font-semibold" data-testid={`builder-module-title-${mi}`} />
                  <Button onClick={() => delModule(mi)} size="icon" variant="ghost" className="text-destructive shrink-0"><Trash size={16} /></Button>
                </div>
                <div className="space-y-3 pl-6">
                  {m.lessons.map((l, li) => (
                    <div key={l.id} className="bg-white border border-border rounded-md p-3" data-testid={`builder-lesson-${mi}-${li}`}>
                      <div className="flex flex-wrap gap-2 items-center">
                        <Input value={l.title} onChange={(e) => updLesson(mi, li, { title: e.target.value })} placeholder="Título de la lección" className="flex-1 min-w-[180px]" />
                        <Select value={l.type} onValueChange={(v) => updLesson(mi, li, { type: v })}>
                          <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-white"><SelectItem value="video">Video</SelectItem><SelectItem value="text">Texto</SelectItem><SelectItem value="file">Archivo</SelectItem></SelectContent>
                        </Select>
                        <Input type="number" value={l.duration_min} onChange={(e) => updLesson(mi, li, { duration_min: e.target.value })} className="w-20" title="minutos" />
                        <Button onClick={() => delLesson(mi, li)} size="icon" variant="ghost" className="text-destructive"><Trash size={15} /></Button>
                      </div>
                      <div className="mt-2">
                        {l.type === "video" && (
                          <div>
                            <div className="flex gap-2 items-center">
                              <Input value={l.video_url} onChange={(e) => updLesson(mi, li, { video_url: e.target.value })} placeholder="URL del video (.mp4) o sube el archivo" className="flex-1" />
                              <label><input type="file" accept="video/*" className="hidden" onChange={(e) => e.target.files[0] && uploadFile(mi, li, e.target.files[0])} data-testid={`builder-upload-video-${mi}-${li}`} /><Button type="button" variant="outline" size="sm" className="gap-1" asChild><span><UploadSimple size={14} /> Subir video</span></Button></label>
                            </div>
                            {l.video_url && <video src={l.video_url} controls className="mt-2 w-full max-w-sm rounded-md border border-border" data-testid={`builder-video-preview-${mi}-${li}`} />}
                          </div>
                        )}
                        {l.type === "text" && (
                          <Textarea value={l.content} onChange={(e) => updLesson(mi, li, { content: e.target.value })} placeholder="Contenido de la lección..." className="text-sm" />
                        )}
                        {l.type === "file" && (
                          <div className="flex gap-2 items-center">
                            <span className="text-sm text-muted-foreground flex items-center gap-1 flex-1"><Paperclip size={14} /> {l.file_name || "Ningún archivo"}</span>
                            <label><input type="file" className="hidden" onChange={(e) => e.target.files[0] && uploadFile(mi, li, e.target.files[0])} /><Button type="button" variant="outline" size="sm" className="gap-1" asChild><span><UploadSimple size={14} /> Subir archivo</span></Button></label>
                          </div>
                        )}
                        <div className="mt-3 border-t border-border pt-2">
                          <p className="text-xs uppercase tracking-overline text-muted-foreground font-mono mb-1">Biblioteca de recursos (PDF/guías)</p>
                          {(l.resources || []).map((r, ri) => (
                            <div key={ri} className="flex items-center gap-2 text-sm py-0.5" data-testid={`builder-resource-${mi}-${li}-${ri}`}>
                              <Paperclip size={13} className="text-primary shrink-0" /><span className="flex-1 truncate">{r.name}</span>
                              <Button type="button" onClick={() => delResource(mi, li, ri)} size="icon" variant="ghost" className="text-destructive h-7 w-7"><Trash size={13} /></Button>
                            </div>
                          ))}
                          <label><input type="file" className="hidden" onChange={(e) => e.target.files[0] && uploadResource(mi, li, e.target.files[0])} data-testid={`builder-add-resource-${mi}-${li}`} /><Button type="button" variant="ghost" size="sm" className="gap-1 text-primary" asChild><span><Plus size={13} /> Adjuntar PDF/guía</span></Button></label>
                        </div>
                      </div>
                    </div>
                  ))}
                  <Button onClick={() => addLesson(mi)} variant="ghost" size="sm" className="gap-1 text-primary" data-testid={`builder-add-lesson-${mi}`}><Plus size={14} /> Agregar lección</Button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Quiz */}
        <section className="bg-white border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-800 tracking-tight flex items-center gap-2"><ListChecks size={20} className="text-primary" /> Evaluación final</h2>
            <Button onClick={addQ} variant="outline" size="sm" className="gap-2" data-testid="builder-add-question"><Plus size={16} /> Pregunta</Button>
          </div>
          <div className="space-y-4">
            {form.quiz.map((q, qi) => (
              <div key={q.id} className="border border-border rounded-lg p-4" data-testid={`builder-question-${qi}`}>
                <div className="flex gap-2 items-center mb-3">
                  <span className="font-semibold text-sm">{qi + 1}.</span>
                  <Input value={q.text} onChange={(e) => updQ(qi, { text: e.target.value })} placeholder="Enunciado de la pregunta" className="flex-1" />
                  <Select value={q.type} onValueChange={(v) => updQ(qi, { type: v, correct_index: 0 })}>
                    <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white"><SelectItem value="multiple">Selección múltiple</SelectItem><SelectItem value="truefalse">Verdadero/Falso</SelectItem></SelectContent>
                  </Select>
                  <Button onClick={() => delQ(qi)} size="icon" variant="ghost" className="text-destructive"><Trash size={15} /></Button>
                </div>
                <RadioGroup value={q.correct_index.toString()} onValueChange={(v) => updQ(qi, { correct_index: parseInt(v) })}>
                  {(q.type === "truefalse" ? ["Verdadero", "Falso"] : q.options).map((opt, oi) => (
                    <div key={oi} className="flex items-center gap-2">
                      <RadioGroupItem value={oi.toString()} id={`${q.id}-${oi}`} title="Marcar como correcta" />
                      {q.type === "truefalse"
                        ? <Label htmlFor={`${q.id}-${oi}`} className="flex-1">{opt}</Label>
                        : <Input value={opt} onChange={(e) => updQ(qi, { options: q.options.map((o, i) => i === oi ? e.target.value : o) })} placeholder={`Opción ${oi + 1}`} className="flex-1 h-9" />}
                    </div>
                  ))}
                </RadioGroup>
                <p className="text-xs text-muted-foreground mt-2">Marca el círculo de la respuesta correcta.</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
